import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Check, FileText, Download, Archive, LayoutDashboard, AlertTriangle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatKina } from "@/lib/money";
import { SUCCESS } from "@/constants/testIds";

function DownloadBtn({ href, label, icon: Icon, tid, filename }) {
    const download = async () => {
        const token = localStorage.getItem("mss_token");
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${href}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };
    return (
        <button
            data-testid={tid}
            onClick={download}
            className="w-full flex items-center justify-between gap-3 border border-neutral-200 bg-white px-4 py-3.5 hover:border-[color:var(--kp-red)] transition-colors rounded-[3px] text-left"
        >
            <span className="flex items-center gap-3">
                <Icon size={16} className="text-neutral-400" />
                <span className="text-[13px] text-[color:var(--kp-charcoal)] font-medium">{label}</span>
            </span>
            <span className="text-[10px] uppercase tracking-[0.14em] font-display font-semibold" style={{ color: "var(--kp-red)" }}>
                Download →
            </span>
        </button>
    );
}

// Bank CSV button — fetches the JSON envelope so the excluded list is
// materialised alongside the file, and downloads from a client-side blob.
// The two-endpoint pattern (JSON preview + raw .csv) exists so a bank-facing
// automation script can still take the byte-clean .csv on its own, but the
// operator UI is wired through the envelope so the excluded list cannot be
// seen without also seeing the file, and vice versa.
function BankCsvButton({ id, filename, onEnvelope }) {
    const download = async () => {
        const token = localStorage.getItem("mss_token");
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pay-periods/${id}/bank-file`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const env = await res.json();
        onEnvelope && onEnvelope(env);
        // The blob is built from env.csv, which the backend guarantees is
        // byte-identical to what the raw .csv endpoint returns. Downloading
        // from here means Mike opens exactly the bytes the app is reporting.
        const blob = new Blob([env.csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = env.filename || filename;
        a.click();
        URL.revokeObjectURL(url);
    };
    return (
        <button
            data-testid={SUCCESS.dlBank}
            onClick={download}
            className="w-full flex items-center justify-between gap-3 border border-neutral-200 bg-white px-4 py-3.5 hover:border-[color:var(--kp-red)] transition-colors rounded-[3px] text-left"
        >
            <span className="flex items-center gap-3">
                <Download size={16} className="text-neutral-400" />
                <span className="text-[13px] text-[color:var(--kp-charcoal)] font-medium">Bank payment file (.csv)</span>
            </span>
            <span className="text-[10px] uppercase tracking-[0.14em] font-display font-semibold" style={{ color: "var(--kp-red)" }}>
                Download →
            </span>
        </button>
    );
}

function BankExcludedBanner({ envelope }) {
    if (!envelope) return null;
    const { excluded = [], payee_count = 0 } = envelope;
    // Nothing to say if every would-be payee made it into the file AND
    // at least one person is being paid. The banner exists for the
    // operator-visible-failures cases only.
    if (excluded.length === 0 && payee_count > 0) return null;

    const zero = payee_count === 0;
    return (
        <div
            data-testid={SUCCESS.bankExcludedBanner}
            className="mt-3 border rounded-[3px] px-4 py-3"
            style={{
                borderColor: zero ? "var(--kp-red)" : "#E5A100",
                background: zero ? "rgba(227,6,19,0.06)" : "rgba(229,161,0,0.08)",
            }}
        >
            <div className="flex items-start gap-3">
                <AlertTriangle
                    size={16}
                    className="mt-0.5"
                    style={{ color: zero ? "var(--kp-red)" : "#B57800" }}
                />
                <div className="flex-1">
                    <div
                        className="text-[10px] uppercase tracking-[0.18em] font-display font-semibold"
                        style={{ color: zero ? "var(--kp-red)" : "#B57800" }}
                    >
                        {zero
                            ? "Bank file empty — nothing to upload"
                            : `Excluded from this file · ${excluded.length}`}
                    </div>
                    <div
                        data-testid={SUCCESS.bankPayeeCount}
                        className="text-[11px] text-neutral-500 mt-0.5"
                    >
                        {payee_count} payee{payee_count === 1 ? "" : "s"} in file
                    </div>
                    {excluded.length > 0 && (
                        <ul className="mt-2 space-y-1">
                            {excluded.map((e) => (
                                <li
                                    key={e.employee_id || e.name}
                                    data-testid={SUCCESS.bankExcludedItem(e.employee_id || e.name)}
                                    className="text-[13px] text-[color:var(--kp-charcoal)] flex justify-between gap-3"
                                >
                                    <span>
                                        <span className="font-medium">{e.name}</span>
                                        <span className="text-neutral-500 text-[11px] ml-2">
                                            missing {(e.missing || []).join(" + ")}
                                        </span>
                                    </span>
                                    <span className="font-mono-money text-[12px] text-neutral-500">
                                        {formatKina(e.net_pay || 0)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

// Round 16 · Westpac PNG Direct Entry download.
// Same two-endpoint pattern as bank CSV: JSON preview materialises the
// excluded list + config-error alongside the file so the operator can
// see the audit envelope BEFORE saving bytes. If preview reports the
// settings are missing, the button doesn't attempt the download —
// it toasts and points the operator at Payroll Settings.
function WestpacDeButton({ id, filename, onEnvelope }) {
    const download = async () => {
        const token = localStorage.getItem("mss_token");
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pay-periods/${id}/westpac-de/preview`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const env = await res.json();
        onEnvelope && onEnvelope(env);
        if (!env.config_ok) {
            // Refuse to download until settings are complete. The banner
            // rendered below surfaces the specific missing settings so
            // Mike walks straight to Payroll Settings to fix them.
            return;
        }
        if (env.credit_count === 0) {
            // Same discipline as bank CSV: no bytes when zero valid credits.
            return;
        }
        // The bytes come from the raw endpoint. The envelope has already
        // been fetched so any excluded list is visible before the file is.
        const bytesRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pay-periods/${id}/westpac-de`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!bytesRes.ok) return;
        const blob = await bytesRes.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = env.filename || filename;
        a.click();
        URL.revokeObjectURL(url);
    };
    return (
        <button
            data-testid={SUCCESS.dlWestpacDe}
            onClick={download}
            className="w-full flex items-center justify-between gap-3 border border-neutral-200 bg-white px-4 py-3.5 hover:border-[color:var(--kp-red)] transition-colors rounded-[3px] text-left"
        >
            <span className="flex items-center gap-3">
                <Download size={16} className="text-neutral-400" />
                <span className="text-[13px] text-[color:var(--kp-charcoal)] font-medium">Westpac Direct Entry (.txt) · UPLOAD FILE</span>
            </span>
            <span className="text-[10px] uppercase tracking-[0.14em] font-display font-semibold" style={{ color: "var(--kp-red)" }}>
                Download →
            </span>
        </button>
    );
}

function WestpacExcludedBanner({ envelope }) {
    if (!envelope) return null;
    const { excluded = [], warnings = [], credit_count = 0,
            config_ok, config_error } = envelope;
    // Config is the loudest failure — the file cannot be built at all.
    if (!config_ok) {
        return (
            <div
                data-testid={SUCCESS.westpacConfigMissing}
                className="mt-3 border rounded-[3px] px-4 py-3"
                style={{ borderColor: "var(--kp-red)", background: "rgba(227,6,19,0.06)" }}
            >
                <div className="flex items-start gap-3">
                    <AlertTriangle size={16} className="mt-0.5" style={{ color: "var(--kp-red)" }} />
                    <div className="flex-1">
                        <div className="text-[10px] uppercase tracking-[0.18em] font-display font-semibold"
                             style={{ color: "var(--kp-red)" }}>
                            Westpac DE not configured — cannot generate
                        </div>
                        <div className="text-[13px] text-[color:var(--kp-charcoal)] mt-1">{config_error}</div>
                        <div className="text-[11px] text-neutral-500 mt-2">
                            Fill in the seven fields under <em>Settings · Payroll rules · Westpac PNG Direct Entry file</em>.
                            The generator refuses to substitute Westpac's sample values.
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    if (excluded.length === 0 && warnings.length === 0 && credit_count > 0) return null;
    const zero = credit_count === 0;
    return (
        <div
            data-testid={SUCCESS.westpacExcludedBanner}
            className="mt-3 border rounded-[3px] px-4 py-3"
            style={{
                borderColor: zero ? "var(--kp-red)" : "#E5A100",
                background: zero ? "rgba(227,6,19,0.06)" : "rgba(229,161,0,0.08)",
            }}
        >
            <div className="flex items-start gap-3">
                <AlertTriangle size={16} className="mt-0.5" style={{ color: zero ? "var(--kp-red)" : "#B57800" }} />
                <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-display font-semibold"
                         style={{ color: zero ? "var(--kp-red)" : "#B57800" }}>
                        {zero ? "Westpac DE would be empty — nothing to upload"
                              : `Excluded from Westpac DE · ${excluded.length}`}
                    </div>
                    <div data-testid={SUCCESS.westpacCreditCount}
                         className="text-[11px] text-neutral-500 mt-0.5">
                        {credit_count} credit line{credit_count === 1 ? "" : "s"} in file
                    </div>
                    {excluded.length > 0 && (
                        <ul className="mt-2 space-y-1">
                            {excluded.map((e) => (
                                <li
                                    key={e.employee_id || e.name}
                                    data-testid={SUCCESS.westpacExcludedItem(e.employee_id || e.name)}
                                    className="text-[13px] text-[color:var(--kp-charcoal)] flex justify-between gap-3"
                                >
                                    <span>
                                        <span className="font-medium">{e.name}</span>
                                        <span className="text-neutral-500 text-[11px] ml-2">{e.reason} — {e.detail}</span>
                                    </span>
                                    <span className="font-mono-money text-[12px] text-neutral-500">
                                        {formatKina(e.payable || e.net_pay || 0)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {warnings.length > 0 && (
                        <div className="mt-2 text-[11px] text-neutral-500">
                            {warnings.length} account title{warnings.length === 1 ? "" : "s"} truncated to 32 chars.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function PayRunSuccess() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = !!(user && (user.is_admin === true || user.role === "admin"));
    const [period, setPeriod] = useState(null);
    const [lines, setLines] = useState([]);
    const [bankEnvelope, setBankEnvelope] = useState(null);
    const [westpacEnvelope, setWestpacEnvelope] = useState(null);
    // 2026-11-05 · manual payment: no bank/Westpac files, show net to pay.
    const [payMethod, setPayMethod] = useState(null);
    useEffect(() => {
        api.get("/payroll/settings")
            .then(({ data }) => setPayMethod(data?.payment_method?.value || "manual"))
            .catch(() => setPayMethod("manual"));
    }, []);
    const manualPay = payMethod !== "bank_file";
    // Round 21 · S2 lodgement chip + reopen dialog.
    const [lodgement, setLodgement] = useState(null);
    const [reopenOpen, setReopenOpen] = useState(false);
    const [reopenNote, setReopenNote] = useState("");
    const [reopening, setReopening] = useState(false);

    useEffect(() => {
        api.get(`/pay-periods/${id}`).then(({ data }) => {
            setPeriod(data.period);
            setLines(data.lines);
        });
    }, [id]);

    // Round 21 · fetch lodgement for this period's month so the Lodged
    // chip and the ack requirement of Reopen come from live data.
    useEffect(() => {
        const month = (period?.ppe_date || "").slice(0, 7);
        if (!month) return;
        api.get(`/irc/s2/lodgement?month=${month}`)
            .then(({ data }) => setLodgement(data))
            .catch(() => setLodgement(null));
    }, [period?.ppe_date]);

    // Prefetch the envelope so the excluded list shows BEFORE Mike clicks
    // download. If there are people missing from the file, the first way he
    // learns about it is not "I just uploaded a broken payment file".
    useEffect(() => {
        if (!id || payMethod !== "bank_file") return;
        api.get(`/pay-periods/${id}/bank-file`)
            .then(({ data }) => setBankEnvelope(data))
            .catch(() => setBankEnvelope(null));
        // Round 16 · same discipline for Westpac DE. The envelope
        // surfaces config errors, excluded rows and truncation warnings
        // before any bytes are downloaded.
        api.get(`/pay-periods/${id}/westpac-de/preview`)
            .then(({ data }) => setWestpacEnvelope(data))
            .catch(() => setWestpacEnvelope(null));
    }, [id, payMethod]);

    // 2026-11-05 · "Change hours (reopen)" on the locked pay run lands here
    // with ?reopen=1 and opens the reopen dialog straight away.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
        if (isAdmin && new URLSearchParams(window.location.search).get("reopen") === "1") setReopenOpen(true);
    }, [isAdmin]);

    if (!period) return <div className="p-10 text-neutral-500 text-sm">Loading…</div>;
    const totals = period.totals || { gross: 0, tax: 0, net: 0 };

    return (
        <div data-testid={SUCCESS.page} className="p-10 max-w-[1100px]">
            {/* Round 21 · Reopen confirmation dialog. When the period's
                month has a lodged S2, the note is mandatory and the ack
                is sent to the backend so the audit trail records it. */}
            {reopenOpen && (
                <div data-testid={SUCCESS.reopenDialog}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-[3px] w-full max-w-[560px] p-6">
                        <div className="text-[11px] uppercase tracking-[0.18em] font-display font-semibold mb-2"
                             style={{ color: "var(--kp-red)" }}>Reopen pay run · PPE {period.ppe_date}</div>
                        {lodgement ? (
                            <div className="mb-3 border rounded-[3px] px-3 py-2 text-[12px]"
                                style={{ borderColor: "var(--kp-red)", background: "rgba(227,6,19,0.06)", color: "#8B0F17" }}>
                                <strong>{period.ppe_date.slice(0, 7)} S2 already lodged with IRC</strong> on
                                {" "}{lodgement.lodged_at?.slice(0, 10)} by {lodgement.lodged_by}.
                                Reopening this period will cause an S6 reconciliation break. A note is required.
                            </div>
                        ) : (
                            <p className="text-[13px] text-[color:var(--kp-charcoal)] mb-3">
                                The pay run will move back to draft so you can correct any worker&rsquo;s hours,
                                then approve it again to make fresh payslips. Leave movements for this period are reversed;
                                re-approving will push fresh movements. No S2 is lodged for this month, so IRC is
                                not affected.
                            </p>
                        )}
                        <textarea data-testid={SUCCESS.reopenNoteInput}
                            className="w-full border border-neutral-300 rounded-[3px] p-3 text-[13px] font-mono-money"
                            rows={3}
                            placeholder={lodgement ? "Reason for reopening — kept on the S2 audit trail forever" : "Optional note"}
                            value={reopenNote}
                            onChange={(e) => setReopenNote(e.target.value)} />
                        {lodgement && (
                            <div className="mt-1 text-[11px] text-neutral-500">
                                {reopenNote.trim().length} chars · required when a lodgement exists
                            </div>
                        )}
                        <div className="mt-4 flex justify-end gap-2">
                            <button data-testid={SUCCESS.reopenCancelBtn}
                                onClick={() => { setReopenOpen(false); setReopenNote(""); }}
                                className="px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] border border-neutral-300 rounded-[3px]">
                                Cancel
                            </button>
                            <button data-testid={SUCCESS.reopenConfirmBtn}
                                disabled={reopening || (lodgement && reopenNote.trim().length < 3)}
                                onClick={async () => {
                                    setReopening(true);
                                    try {
                                        const body = lodgement
                                            ? { lodged_s2_acknowledged: true, lodged_s2_ack_note: reopenNote.trim() }
                                            : {};
                                        await api.post(`/pay-periods/${id}/reopen`, body);
                                        toast.success(`Pay run reopened (draft)`);
                                        setReopenOpen(false); setReopenNote("");
                                        navigate(`/pay-runs/${id}`);
                                    } catch (ex) {
                                        toast.error(formatApiError(ex?.response?.data?.detail) || "Reopen failed");
                                    } finally { setReopening(false); }
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] disabled:opacity-40"
                                style={{ background: "var(--kp-red)" }}>
                                <RotateCcw size={12} /> Reopen
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Success hero */}
            <div className="relative rounded-[3px] p-8" style={{ background: "var(--kp-black)", color: "#fff" }}>
                <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: "var(--kp-success)" }} />
                <div className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-[2px]" style={{ background: "var(--kp-success)" }}>
                        <Check size={14} strokeWidth={3} />
                    </span>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-400 font-display">Pay run complete</div>
                </div>
                <div className="font-display font-bold text-4xl mt-3">
                    PPE <span className="font-mono-money">{period.ppe_date}</span> · locked
                </div>
                <div className="text-neutral-400 text-sm mt-1">
                    Approved by {period.approved_by} · {period.approved_at?.slice(0, 16).replace("T", " ")} UTC
                </div>

                {/* Round 21 · Lodged chip + Reopen action (admin only). */}
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {lodgement && (
                        <span data-testid={SUCCESS.lodgedChip}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-display font-semibold uppercase tracking-[0.14em] rounded-[2px]"
                            style={{ background: "#fff", color: "var(--kp-charcoal)" }}>
                            S2 lodged · {lodgement.lodged_at?.slice(0, 10)}
                        </span>
                    )}
                    {isAdmin && (
                        <button data-testid={SUCCESS.reopenBtn}
                            onClick={() => setReopenOpen(true)}
                            className="inline-flex items-center gap-1 px-3 py-1 text-[10px] font-display font-semibold uppercase tracking-[0.14em] rounded-[2px] border"
                            style={{ borderColor: "#FF6B75", color: "#FF6B75", background: "rgba(255,107,117,0.08)" }}>
                            <RotateCcw size={11} /> Reopen to change hours
                        </button>
                    )}
                </div>

                <div className="grid grid-cols-3 gap-8 mt-6">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display">Gross</div>
                        <div className="font-display font-bold text-3xl mt-1 font-mono-money">{formatKina(totals.gross)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display">Tax to IRC</div>
                        <div className="font-display font-bold text-3xl mt-1 font-mono-money" style={{ color: "#FF6B75" }}>{formatKina(totals.tax)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display">Net paid</div>
                        <div className="font-display font-bold text-3xl mt-1 font-mono-money" style={{ color: "#59C783" }}>{formatKina(totals.net)}</div>
                    </div>
                </div>
            </div>

            {/* Downloads */}
            <div className="mt-8">
                <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 font-display mb-3">Downloads</div>
                <div className="grid grid-cols-2 gap-3">
                    <DownloadBtn tid={SUCCESS.dlPayslipsZip} href={`/api/pay-periods/${id}/payslips.zip`} filename={`payslips_${period.ppe_date}.zip`} icon={Archive} label="All payslips (.zip)" />
                    {!manualPay && <BankCsvButton id={id} filename={`bank_file_${period.ppe_date}.csv`} onEnvelope={setBankEnvelope} />}
                    {!manualPay && <WestpacDeButton id={id} filename={`westpac_de_${period.ppe_date}.txt`} onEnvelope={setWestpacEnvelope} />}
                    <DownloadBtn tid={SUCCESS.dlIrc} href={`/api/pay-periods/${id}/irc-remittance.pdf`} filename={`irc_remittance_${period.ppe_date}.pdf`} icon={FileText} label="IRC remittance summary (.pdf)" />
                    <DownloadBtn tid={SUCCESS.dlPayRun} href={`/api/pay-periods/${id}/pay-run.pdf`} filename={`pay_run_${period.ppe_date}.pdf`} icon={FileText} label="Locked pay-run report (.pdf)" />
                </div>
                {!manualPay && <BankExcludedBanner envelope={bankEnvelope} />}
                {!manualPay && <WestpacExcludedBanner envelope={westpacEnvelope} />}
            </div>

            {/* Individual payslips */}
            <div className="mt-8">
                <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 font-display mb-1">
                    {manualPay ? "Net pay to pay each worker" : "Individual payslips"}
                </div>
                {manualPay && (
                    <p data-testid="success-manual-pay-note" className="text-[12px] text-neutral-600 mb-3">
                        Pay each worker the net amount shown, from your own banking. Total to pay:{" "}
                        <span className="font-mono-money font-semibold">{formatKina(totals.net)}</span>.
                        Each worker&rsquo;s payslip is on the right.
                    </p>
                )}
                <div className="bg-white border border-neutral-200 rounded-[3px] divide-y divide-neutral-100">
                    {lines.map((ln) => {
                        const emp = ln.employee_snapshot || ln.employee || {};
                        const net = ln.calc?.net_pay || 0;
                        const paid = net > 0;
                        return (
                            <div key={ln.id} className="flex items-center justify-between px-4 py-3">
                                <div>
                                    <div className="text-[13px] text-[color:var(--kp-charcoal)] font-medium">{emp.name}</div>
                                    <div className="text-[11px] text-neutral-400">
                                        {emp.position || "—"} · net{" "}
                                        <span className="font-mono-money" style={{ color: paid ? "var(--kp-success)" : "var(--kp-muted)" }}>
                                            {formatKina(net)}
                                        </span>
                                        {!paid && <span className="ml-2 uppercase tracking-[0.14em] font-display font-semibold text-[10px] text-neutral-400">no payslip</span>}
                                    </div>
                                </div>
                                {paid && (
                                    <DownloadBtnCompact
                                        tid={SUCCESS.dlPayslip(ln.employee_id)}
                                        href={`/api/pay-periods/${id}/payslips/${ln.employee_id}.pdf`}
                                        filename={`payslip_${(emp.name || "employee").replace(/\s+/g, "_")}_${period.ppe_date}.pdf`}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="mt-8">
                <button
                    onClick={() => navigate("/")}
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-[12px] font-display font-semibold uppercase tracking-[0.12em] border border-neutral-300 text-neutral-700 hover:bg-neutral-100 rounded-[3px]"
                >
                    <LayoutDashboard size={14} /> Back to dashboard
                </button>
            </div>
        </div>
    );
}

function DownloadBtnCompact({ href, filename, tid }) {
    const download = async () => {
        const token = localStorage.getItem("mss_token");
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${href}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };
    return (
        <button
            data-testid={tid}
            onClick={download}
            className="text-[11px] font-display font-semibold uppercase tracking-[0.14em] px-3 py-1.5 border rounded-[3px]"
            style={{ borderColor: "var(--kp-red)", color: "var(--kp-red)" }}
        >
            .pdf ↓
        </button>
    );
}
